import { Color, Flex, formatBytes, Icon, IconButton, ProgressBar, Spacing, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { FecImportFileError } from '../../../../enums/fecImportFileError'
import * as Styled from './styles'
import { FecImportFileListItem } from './types'

export interface FecImportFileListProps {
  items: FecImportFileListItem[]
}

const ERROR_TRANSLATION_KEYS: Record<FecImportFileError, string> = {
  [FecImportFileError.AlreadyImported]:
    'settings.organization.accounting.fec_file_import.modal.upload_step.already_imported_error',
  [FecImportFileError.FileTooLarge]:
    'settings.organization.accounting.fec_file_import.modal.upload_step.error.file_too_large',
  [FecImportFileError.InvalidFormat]:
    'settings.organization.accounting.fec_file_import.modal.upload_step.error.invalid_format',
  [FecImportFileError.UploadFailed]:
    'settings.organization.accounting.fec_file_import.modal.upload_step.upload_failed_error',
}

const isBlockingFecImportFileError = (error?: FecImportFileError): boolean =>
  error === FecImportFileError.FileTooLarge || error === FecImportFileError.InvalidFormat

const getFileItemIconProps = (hasBlockingError: boolean, hasWarning: boolean) => {
  if (hasBlockingError) {
    return { color: Color.Red700, icon: 'fileXCorner' as const }
  }

  if (hasWarning) {
    return { color: Color.Yellow800, icon: 'triangleAlert' as const }
  }

  return { color: undefined, icon: 'fileText' as const }
}

export const FecImportFileList = ({ items }: FecImportFileListProps): ReactElement => {
  const { t } = useTranslation()

  return (
    <Flex flexDirection="column" gap={Spacing.S8}>
      {items.map(({ disabled, error, fileName, fileSize, onRemove, progressPercent }, index) => {
        const hasBlockingError = isBlockingFecImportFileError(error)
        const hasWarning = !!error && !hasBlockingError
        const fileItemIcon = getFileItemIconProps(hasBlockingError, hasWarning)

        return (
          <Styled.FileItemGroup key={`${fileName}-${index}`}>
            <Styled.FileItem $hasError={hasBlockingError}>
              <Styled.FileIconWrapper>
                <Icon color={fileItemIcon.color} icon={fileItemIcon.icon} size="md" />
              </Styled.FileIconWrapper>
              <Styled.FileNameContainer>
                <Text colorVariant="primary" title={fileName} truncate variant="bodySM">
                  {fileName}
                </Text>
                {progressPercent !== undefined ? (
                  <>
                    <Styled.FileUploadProgress aria-label={fileName} max={100} value={progressPercent} />
                    <ProgressBar
                      backgroundColor={Color.Grey100}
                      progress={progressPercent}
                      progressColor={Color.Lavender500}
                      size="small"
                      withAnimation
                    />
                  </>
                ) : (
                  fileSize !== undefined && (
                    <Text colorVariant="discreet" variant="bodySM">
                      {formatBytes(fileSize)}
                    </Text>
                  )
                )}
              </Styled.FileNameContainer>
              {onRemove && (
                <IconButton
                  aria-label={t('settings.organization.accounting.fec_file_import.modal.upload_step.remove_file', {
                    fileName,
                  })}
                  disabled={disabled}
                  icon="x"
                  onClick={onRemove}
                  type="button"
                />
              )}
            </Styled.FileItem>
            {error && (
              <Text
                color={hasWarning ? Color.Yellow800 : undefined}
                colorVariant={hasWarning ? undefined : 'danger'}
                role="alert"
                variant="bodySM"
              >
                {t(ERROR_TRANSLATION_KEYS[error])}
              </Text>
            )}
          </Styled.FileItemGroup>
        )
      })}
    </Flex>
  )
}
